/* eslint-disable react-refresh/only-export-components -- app router setup module, not a reusable component file */
import React, { lazy, type ReactNode, Suspense, Component, ErrorInfo } from "react";
import { createBrowserRouter, Outlet } from "react-router-dom";
import { userRoutes } from "./user-routes";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { Button } from '@werkstatt/ui';
import { RefreshCw } from "lucide-react";

import { logger } from "./utils/logger";
// Lazy Loading Error Boundary with Retry
class LazyErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; retryCount: number }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("Lazy loading error:", error, errorInfo);

    // Auto-retry once for chunk loading errors
    if (this.state.retryCount < 1 && error.message.includes('Failed to fetch dynamically imported module')) {
      setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          retryCount: prev.retryCount + 1
        }));
      }, 100);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, retryCount: 0 });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center max-w-md">
            <div className="text-red-600 mb-4">
              <RefreshCw className="h-12 w-12 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Fehler beim Laden</h2>
            <p className="text-muted-foreground mb-4">
              Die Seite konnte nicht geladen werden. Bitte laden Sie die Seite neu.
            </p>
            <Button onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Seite neu laden
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const SuspenseWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <LazyErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-sm text-muted-foreground">Seite wird geladen...</p>
            </div>
          </div>
        }
      >
        {children}
      </Suspense>
    </LazyErrorBoundary>
  );
};

const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const SomethingWentWrongPage = lazy(
  () => import("./pages/SomethingWentWrongPage"),
);

export const router = createBrowserRouter(
  [
    {
      element: (
        <ErrorBoundary>
          <SuspenseWrapper>
            <Outlet />
          </SuspenseWrapper>
        </ErrorBoundary>
      ),
      children: userRoutes
    },
    {
      path: "*",
      element: (
        <SuspenseWrapper>
          <NotFoundPage />
        </SuspenseWrapper>
      ),
      errorElement: (
        <SuspenseWrapper>
          <SomethingWentWrongPage />
        </SuspenseWrapper>
      ),
    },
  ]
);